import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getImageUrl } from "@/lib/storage";
import { formatPrice, type Currency, OPERATION_LABEL, PROPERTY_TYPE_LABEL } from "@/lib/format";
import { LeadContactForm } from "@/components/public/LeadContactForm";
import { PropertyCard, type PropertyCardData } from "@/components/public/PropertyCard";
import { buildHead, pickLang, localized, type SeoLang } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { BedDouble, Bath, Car, Ruler, MapPin, Star, MessageCircle, ChevronLeft, ChevronRight, X } from "lucide-react";

const P_COPY: Record<SeoLang, Record<string, string>> = {
  es: { home: "Inicio", featured: "Destacada", opportunity: "Oportunidad", beds: "Dormitorios", baths: "Baños", garage: "Cocheras", area: "Superficie", description: "Descripción", amenities: "Amenities", askAbout: "Consultá por esta propiedad", reply: "Te respondemos en menos de 24h.", whatsapp: "WhatsApp directo", similar: "Propiedades similares", nophotos: "Sin fotos disponibles", wa: "Hola, me interesa la propiedad" },
  en: { home: "Home", featured: "Featured", opportunity: "Opportunity", beds: "Bedrooms", baths: "Bathrooms", garage: "Parking", area: "Area", description: "Description", amenities: "Amenities", askAbout: "Ask about this property", reply: "We reply within 24 hours.", whatsapp: "Chat on WhatsApp", similar: "Similar properties", nophotos: "No photos available", wa: "Hi, I am interested in the property" },
  pt: { home: "Início", featured: "Destaque", opportunity: "Oportunidade", beds: "Quartos", baths: "Banheiros", garage: "Vagas", area: "Área", description: "Descrição", amenities: "Comodidades", askAbout: "Consulte sobre este imóvel", reply: "Respondemos em até 24 horas.", whatsapp: "WhatsApp direto", similar: "Imóveis semelhantes", nophotos: "Sem fotos disponíveis", wa: "Olá, tenho interesse no imóvel" },
};

async function fetchProperty(slug: string) {
  const { data, error } = await supabase
    .from("properties")
    .select("*, location:locations(name, slug, type)")
    .eq("slug", slug)
    .neq("status", "borrador")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw notFound();
  const { data: images } = await supabase
    .from("property_images")
    .select("id, url, alt_text, sort_order")
    .eq("property_id", data.id)
    .order("sort_order");
  return { property: data, images: images ?? [] };
}

export const Route = createFileRoute("/_public/propiedad/$slug")({
  loader: ({ params }) => fetchProperty(params.slug),
  component: PropertyDetail,
  head: ({ loaderData, match }) => {
    const lang = pickLang(match.search);
    if (!loaderData) {
      return { meta: [{ title: "Propiedad no encontrada" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.property as Record<string, unknown>;
    const slug = p["slug"] as string;
    const baseTitle = localized(p, "title", lang) ?? "";
    const title = localized(p, "seo_title", lang) || `${baseTitle} — Dahiana Marín`;
    const desc = localized(p, "description", lang);
    const description =
      localized(p, "seo_description", lang) ||
      (desc?.slice(0, 160) ??
        `${OPERATION_LABEL[p["operation"] as string] ?? p["operation"]} · ${formatPrice(
          p["price_amount"] as number,
          p["price_currency"] as Currency,
        )}`);
    const firstImage = loaderData.images?.[0]?.url ?? null;
    const ogImage = firstImage && /^https?:\/\//.test(firstImage) ? firstImage : undefined;
    const { meta, links, url } = buildHead({
      lang,
      path: `/propiedad/${slug}`,
      title,
      description,
      ogType: "website",
      image: ogImage,
    });
    // Ficha inmobiliaria: usamos RealEstateListing (Product/Offer exige campos
    // de comercio electrónico que un inmueble no cumple y Google los reporta).
    const priceAmount = Number(p["price_amount"]);
    const hasPrice = Number.isFinite(priceAmount) && priceAmount > 0;
    const images = (loaderData.images ?? [])
      .map((i) => i.url)
      .filter((u): u is string => typeof u === "string" && /^https?:\/\//.test(u));

    const listing: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: baseTitle,
      description,
      url,
      inLanguage: lang === "es" ? "es-PY" : lang === "en" ? "en-US" : "pt-BR",
      datePosted: (p["published_at"] as string | null) ?? (p["created_at"] as string | null) ?? undefined,
      provider: { "@id": "https://dahianamarin.com/#agencia" },
    };
    if (images.length) listing["image"] = images.slice(0, 6);
    if (hasPrice) {
      listing["offers"] = {
        "@type": "Offer",
        price: priceAmount,
        priceCurrency: p["price_currency"],
        availability: "https://schema.org/InStock",
        url,
        seller: { "@id": "https://dahianamarin.com/#agencia" },
      };
    }

    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(listing),
        },
      ],
    };
  },

  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-xl font-semibold">No pudimos cargar la propiedad</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      <Link to="/" className="mt-6 inline-block text-primary underline">Volver al inicio</Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold">Propiedad no disponible</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Puede haber sido vendida, alquilada o retirada del portal.
      </p>
      <Link to="/" className="mt-6 inline-block text-primary underline">Ver otras propiedades</Link>
    </div>
  ),
});

function PropertyDetail() {
  const { property: p, images } = Route.useLoaderData();
  const { lang } = useI18n();
  const c = P_COPY[lang];
  const row = p as Record<string, unknown>;
  const pTitle = localized(row, "title", lang) ?? "";
  const pDescription = localized(row, "description", lang);
  const [signed, setSigned] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    (async () => {
      const urls = await Promise.all(images.map((i: { url: string }) => getImageUrl(i.url, { width: 1280, quality: 75 })));
      setSigned(urls);
    })();
  }, [images]);

  useEffect(() => {
    // Increment view count (fire-and-forget)
    supabase
      .from("properties")
      .update({ views_count: (p.views_count ?? 0) + 1 })
      .eq("id", p.id)
      .then(() => {});
  }, [p.id, p.views_count]);

  const { data: related } = useQuery({
    queryKey: ["related", p.id, p.operation, p.property_type],
    queryFn: async () => {
      const { data } = await supabase
        .from("properties")
        .select("id, slug, title, title_en, title_pt, operation, property_type, price_amount, price_currency, address_text, bedrooms, bathrooms, garage_spots, area_m2, is_featured, is_opportunity, amenities")
        .neq("status", "borrador")
        .neq("id", p.id)
        .eq("operation", p.operation)
        .eq("property_type", p.property_type)
        .limit(3);
      return (data ?? []) as PropertyCardData[];
    },
  });

  const opLabel = OPERATION_LABEL[p.operation] ?? p.operation;
  const typeLabel = PROPERTY_TYPE_LABEL[p.property_type]?.singular ?? p.property_type;
  const nextImg = () => setActive((i) => (i + 1) % Math.max(signed.length, 1));
  const prevImg = () => setActive((i) => (i - 1 + Math.max(signed.length, 1)) % Math.max(signed.length, 1));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav className="mb-4 text-xs uppercase tracking-wider text-muted-foreground">
        <Link to="/" className="hover:text-foreground">{c.home}</Link>
        <span className="mx-2">·</span>
        <span>{opLabel}</span>
        <span className="mx-2">·</span>
        <span>{typeLabel}</span>
      </nav>

      {/* Gallery */}
      {signed.length > 0 ? (
        <div className="grid gap-2 md:grid-cols-4 md:grid-rows-2 md:h-[480px]">
          <button
            onClick={() => { setActive(0); setLightbox(true); }}
            aria-label="Ver foto principal en pantalla completa"
            className="relative col-span-2 row-span-2 overflow-hidden rounded-xl bg-secondary"
          >
            <img src={signed[0]} alt={pTitle} className="h-full w-full object-cover" />
          </button>
          {signed.slice(1, 5).map((url, i) => (
            <button
              key={i}
              onClick={() => { setActive(i + 1); setLightbox(true); }}
              aria-label={`Ver foto ${i + 2} en pantalla completa`}
              className="relative overflow-hidden rounded-xl bg-secondary"
            >
              <img src={url} alt="" className="h-full w-full object-cover" />
              {i === 3 && signed.length > 5 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-semibold text-white">
                  +{signed.length - 5} fotos
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex h-64 items-center justify-center rounded-xl bg-secondary text-sm text-muted-foreground">
          {c.nophotos}
        </div>
      )}

      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap gap-1.5">
                {p.is_featured && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
                    <Star className="h-3 w-3" /> {c.featured}
                  </span>
                )}
                {p.is_opportunity && (
                  <span className="rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
                    {c.opportunity}
                  </span>
                )}
              </div>
              <h1 className="mt-2 text-2xl font-semibold text-foreground md:text-3xl">{pTitle}</h1>
              {p.address_text && (
                <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" /> {p.address_text}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-foreground md:text-3xl">
                {formatPrice(p.price_amount, p.price_currency as Currency)}
              </div>
              {p.common_expenses != null && (
                <div className="text-xs text-muted-foreground">
                  + Gs. {new Intl.NumberFormat("es-PY").format(p.common_expenses)} exp.
                </div>
              )}
            </div>
          </div>

          {/* Key specs */}
          <div className="mt-6 grid grid-cols-2 gap-3 rounded-xl border border-border bg-card p-4 md:grid-cols-4">
            <Spec icon={<BedDouble className="h-5 w-5" />} label={c.beds} value={p.bedrooms} />
            <Spec icon={<Bath className="h-5 w-5" />} label={c.baths} value={p.bathrooms} />
            <Spec icon={<Car className="h-5 w-5" />} label={c.garage} value={p.garage_spots} />
            <Spec icon={<Ruler className="h-5 w-5" />} label={c.area} value={p.area_m2 ? `${p.area_m2} m²` : null} />
          </div>

          {/* Description */}
          {pDescription && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">{c.description}</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/85">
                {pDescription}
              </p>
            </section>
          )}

          {/* Amenities */}
          {p.amenities && p.amenities.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">{c.amenities}</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.amenities.map((a: string) => (
                  <span key={a} className="rounded-full border border-input bg-secondary/60 px-3 py-1 text-xs">
                    {a}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Contact sidebar */}
        <aside className="lg:sticky lg:top-20 lg:h-fit">
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">{c.askAbout}</div>
            <h3 className="text-base font-semibold">Dahiana Marín</h3>
            <p className="mt-1 text-xs text-muted-foreground">{c.reply}</p>
            <div className="mt-4">
              <LeadContactForm propertyId={p.id} propertyTitle={pTitle} />
            </div>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${c.wa} "${pTitle}"`)}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-medium hover:bg-secondary"
            >
              <MessageCircle className="h-4 w-4" /> {c.whatsapp}
            </a>
          </div>
        </aside>
      </div>

      {/* Related */}
      {related && related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-xl font-semibold">{c.similar}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => <PropertyCard key={r.id} p={r} />)}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightbox && signed.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(false)}>
          <button aria-label="Cerrar galería" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setLightbox(false); }}>
            <X className="h-5 w-5" />
          </button>
          <button aria-label="Foto anterior" className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); prevImg(); }}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <img src={signed[active]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button aria-label="Foto siguiente" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); nextImg(); }}>
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
            {active + 1} / {signed.length}
          </div>
        </div>
      )}
    </div>
  );
}

function Spec({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string | null }) {
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-lg bg-secondary text-primary">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold">{value ?? "—"}</div>
      </div>
    </div>
  );
}
